import React, { useMemo } from 'react';

import classNames from 'classnames';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CircleInfoIcon from 'Assets/icons/circle-info.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import ExclamationTriangleIcon from 'Assets/icons/exclamation-triangle.svg';

const alertTypes = {
    info: {
        icon: <CircleInfoIcon className="tw-w-5 tw-h-5 tw-text-blue" />,
    },
    error: {
        icon: <CircleXMarkIcon className="tw-w-5 tw-h-5 tw-text-red" />,
    },
    success: {
        icon: <CircleCheckIcon className="tw-w-5 tw-h-5 tw-text-green" />,
    },
    warning: {
        icon: <ExclamationTriangleIcon className="tw-w-5 tw-h-5 tw-text-yellow-400" />,
    },
};

/**
 * `role` defaults to the assertive `alert`, which is right for the warnings and
 * errors most callers use. A non-urgent, informational alert should pass
 * `role="status"` instead — assertive live regions interrupt whatever a screen
 * reader is currently saying, which is wrong for something the user did not ask
 * for and does not have to act on.
 */
export default function Alert({ type, children, role = 'alert' } = { type: 'info' }) {
    const { icon } = useMemo(() => alertTypes[type], [type]);

    return (
        <div
            className={classNames('tw-flex tw-items-center tw-w-full tw-p-4 tw-rounded-lg', {
                'tw-text-blue tw-bg-blue-50': type === 'info',
                'tw-text-red tw-bg-red-50': type === 'error',
                'tw-text-green tw-bg-green-50': type === 'success',
                'tw-text-yellow-400 tw-bg-yellow-50': type === 'warning',
            })}
            role={role}
        >
            <div className="tw-inline-flex tw-items-center tw-justify-center tw-flex-shrink-0">{icon}</div>
            <div className="tw-ms-3 tw-text-sm tw-font-normal">{children}</div>
        </div>
    );
}
